/**
 * Billing — E7 (ACTION_PLAN.md §7). Cloned from tengrade's own
 * web-examiner/src/pages/Billing.tsx shape (Stripe Checkout redirect,
 * credit balance display), simplified: no bundles, no role gating —
 * post_confirmation/handler.py's own "org roles = credit-balance
 * ownership only" note means every org member may view and top up,
 * unlike tengrade's Owner/Auditor split.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  ApiError,
  createCheckoutSession,
  getBilling,
  type Billing as BillingDto,
  type ComplexityTier,
} from '../api/client';

const TOP_UP_PRESETS_GBP = [10, 25, 50, 100];

const TIER_ORDER: ComplexityTier[] = ['low', 'medium', 'high', 'extra'];
const TIER_LABEL: Record<ComplexityTier, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  extra: 'Extra',
};

function formatPence(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

function formatWhen(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Billing() {
  const [billing, setBilling] = useState<BillingDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [amountGbp, setAmountGbp] = useState(25);
  const [startingCheckout, setStartingCheckout] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setBilling(await getBilling());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load billing.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function startCheckout() {
    setStartingCheckout(true);
    setError('');
    try {
      const { checkout_url: checkoutUrl } = await createCheckoutSession(Math.round(amountGbp * 100));
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start checkout.');
      setStartingCheckout(false);
    }
  }

  return (
    <main className="page">
      <h1>Billing</h1>
      <p className="muted">
        {billing?.plan === 'free'
          ? 'You are on the free tier — low-complexity jobs only, no charge.'
          : 'Credit funds each generation job, charged upfront based on schema complexity.'}
      </p>

      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : billing ? (
        <>
          <div className="result">
            <p>
              Plan: <strong>{billing.plan === 'free' ? 'Free' : 'Paid'}</strong>
            </p>
            <p>
              Current credit: <strong>{formatPence(billing.credit_balance_pence)}</strong>
            </p>
            <div className="field-inline">
              <label className="field">
                <span className="field-label">Top up (£)</span>
                <select value={amountGbp} onChange={(e) => setAmountGbp(Number(e.target.value))}>
                  {TOP_UP_PRESETS_GBP.map((gbp) => (
                    <option key={gbp} value={gbp}>
                      £{gbp}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="btn btn--primary"
                onClick={() => void startCheckout()}
                disabled={startingCheckout}
              >
                {startingCheckout ? 'Starting checkout…' : 'Top up via Stripe'}
              </button>
            </div>
          </div>

          {billing.plan === 'free' && billing.free_tier && (
            <div className="result">
              <h2>Free tier usage</h2>
              <p>
                <strong>{billing.free_tier.requests_this_month}</strong> of{' '}
                <strong>{billing.free_tier.monthly_request_cap}</strong> requests used this month —{' '}
                {billing.free_tier.requests_remaining_this_month} remaining.
              </p>
              <p className="muted small">
                Free tier jobs must score as "low" complexity and always run on{' '}
                {billing.price_table.low.model}, with no retries. Top up to unlock higher
                complexity and adaptive-effort routing.
              </p>
            </div>
          )}

          <div className="result">
            <h2>Price per job</h2>
            <p className="muted small">
              Every job is priced before it runs, from its schema complexity. Expected and
              charged cost always match — this is what you'll pay before you generate.
            </p>
            <table className="schema-review-table">
              <thead>
                <tr>
                  <th>Complexity</th>
                  <th>Model</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {TIER_ORDER.map((tier) => (
                  <tr key={tier}>
                    <td>{TIER_LABEL[tier]}</td>
                    <td className="muted">{billing.price_table[tier].model}</td>
                    <td>{formatPence(billing.price_table[tier].price_gbp_pence)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="result">
            <h2>Payment history</h2>
            {billing.payments.length === 0 ? (
              <p className="muted small">No top-ups yet.</p>
            ) : (
              <table className="schema-review-table">
                <thead>
                  <tr>
                    <th>Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {billing.payments.map((payment) => (
                    <tr key={payment.credited_at}>
                      <td>{formatPence(payment.amount_pence)}</td>
                      <td className="muted">{formatWhen(payment.credited_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}
    </main>
  );
}
